import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { CheckoutRecap } from "@/components/checkout/CheckoutRecap";
import type { CheckoutFormValues } from "@/domain/checkout";
import { PersonalDetailsForm } from "@/components/checkout/PersonalDetailsForm";
import { pushGtmEvent } from "@/infra/observability/googleTagManager";
import { WebshopLayout } from "@/components/layout/WebshopLayout";
import { useWebshopInvitePatient } from "@/infra/apis/useWebshopInvitePatient";
import { useCmsTherapyPlans } from "@/infra/hooks/useCmsTherapyPlans";
import type { CmsTherapyPlan } from "@/infra/hooks/useCmsTherapyPlans";
import { AppRoutes } from "@/infra/router";
import { useTreatmentSelectionStore } from "@/infra/stores/treatmentSelectionStore";
import { ConflictException } from "@akina/infra";
import { LoadingOverlay, useScreenSizeDetector } from "@akina/presentation";

const UNIVERSAL_KEY = "t_Universal";
type MobileCheckoutStep = "recap" | "details";
type MobileCheckoutStepState = { defaultStep: MobileCheckoutStep; step: MobileCheckoutStep };
type NavigateFunction = ReturnType<typeof useNavigate>;

type UseTreatmentKeySyncParams = {
  treatmentKey: string | null;
  isCmsLoading: boolean;
  plans: CmsTherapyPlan[];
  selectedTemplateKey: string | null;
  selectedBodyPart: string | null;
  navigate: NavigateFunction;
  setTreatmentWithBodyPart: (bodyPart: string, templateKey: string, displayedName: string) => void;
  resetTreatmentSelection: () => void;
};

function redirectToPaymentFailure(navigate: NavigateFunction) {
  navigate(`${AppRoutes.Checkout}?paymentFailed=true`, { replace: true });
}

function redirectToPaymentProvider(transactionUrl: string, navigate: NavigateFunction) {
  try {
    const url = new URL(transactionUrl);
    if (url.protocol === "https:") {
      globalThis.location.href = url.href;
      return;
    }
  } catch {
    redirectToPaymentFailure(navigate);
    return;
  }

  redirectToPaymentFailure(navigate);
}

function getMobileStep(stepState: MobileCheckoutStepState, defaultStep: MobileCheckoutStep): MobileCheckoutStep {
  return stepState.defaultStep === defaultStep ? stepState.step : defaultStep;
}

function useTreatmentKeySync({
  treatmentKey,
  isCmsLoading,
  plans,
  selectedTemplateKey,
  selectedBodyPart,
  navigate,
  setTreatmentWithBodyPart,
  resetTreatmentSelection,
}: UseTreatmentKeySyncParams) {
  useEffect(() => {
    if (!treatmentKey || isCmsLoading) return;

    if (treatmentKey === UNIVERSAL_KEY) {
      setTreatmentWithBodyPart("all", UNIVERSAL_KEY, "");
      navigate(AppRoutes.Result, { replace: true });
      return;
    }

    const plan = plans.find(p => p.original_therapy_plan_key === treatmentKey);
    if (!plan) {
      resetTreatmentSelection();
      navigate(AppRoutes.TreatmentSelection, { replace: true });
      return;
    }

    if (treatmentKey !== selectedTemplateKey) {
      setTreatmentWithBodyPart(plan.checkout_body_part_key || selectedBodyPart || "", treatmentKey, plan.DisplayedName);
    }
  }, [
    treatmentKey,
    isCmsLoading,
    plans,
    selectedTemplateKey,
    selectedBodyPart,
    setTreatmentWithBodyPart,
    navigate,
    resetTreatmentSelection,
  ]);
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentFailed = searchParams.get("paymentFailed") === "true";
  const treatmentKey = searchParams.get("treatment");
  const { isMobile } = useScreenSizeDetector();
  const defaultMobileStep: MobileCheckoutStep = paymentFailed ? "details" : "recap";
  const [mobileStepState, setMobileStepState] = useState<MobileCheckoutStepState>(() => ({
    defaultStep: defaultMobileStep,
    step: defaultMobileStep,
  }));
  const mobileStep = getMobileStep(mobileStepState, defaultMobileStep);

  const selectedBodyPart = useTreatmentSelectionStore(s => s.selectedBodyPart);
  const selectedTemplateKey = useTreatmentSelectionStore(s => s.selectedTemplateKey);
  const personalDetails = useTreatmentSelectionStore(s => s.personalDetails);
  const setPersonalDetails = useTreatmentSelectionStore(s => s.actions.setPersonalDetails);
  const setTreatmentWithBodyPart = useTreatmentSelectionStore(s => s.actions.setTreatmentWithBodyPart);
  const resetTreatmentSelection = useTreatmentSelectionStore(s => s.actions.reset);

  const { plans, isLoading: isCmsLoading } = useCmsTherapyPlans();

  const { mutate: invitePatient, isPending, isError, error, reset } = useWebshopInvitePatient();
  const isEmailConflict = error instanceof ConflictException;

  useTreatmentKeySync({
    treatmentKey,
    isCmsLoading,
    plans,
    selectedTemplateKey,
    selectedBodyPart,
    navigate,
    setTreatmentWithBodyPart,
    resetTreatmentSelection,
  });

  if (treatmentKey === UNIVERSAL_KEY) {
    return <LoadingOverlay />;
  }

  if (!selectedBodyPart || !selectedTemplateKey) {
    return <Navigate to={AppRoutes.TreatmentSelection} replace />;
  }

  const handleSubmit = (values: CheckoutFormValues) => {
    pushGtmEvent("proceeded_to_payment");
    setPersonalDetails(values);
    invitePatient(values, {
      onSuccess: data => redirectToPaymentProvider(data.transactionUrl, navigate),
    });
  };

  const personalDetailsForm = (
    <PersonalDetailsForm
      onSubmit={handleSubmit}
      isPending={isPending}
      isError={(isError && !isEmailConflict) || paymentFailed}
      isEmailConflict={isEmailConflict}
      onEmailChange={reset}
      defaultValues={personalDetails ?? undefined}
    />
  );
  const desktopCheckoutContent = (
    <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
      <CheckoutRecap />
      {personalDetailsForm}
    </div>
  );

  const mobileCheckoutContent = mobileStep === "recap" ? <CheckoutRecap /> : personalDetailsForm;
  const checkoutContent = isMobile ? mobileCheckoutContent : desktopCheckoutContent;
  const backToPreviousPage = () => navigate(AppRoutes.Result);
  const backToRecap = () => setMobileStepState({ defaultStep: defaultMobileStep, step: "recap" });
  const continueToDetails = () => setMobileStepState({ defaultStep: defaultMobileStep, step: "details" });
  const footerBackAction = isMobile && mobileStep === "details" ? backToRecap : backToPreviousPage;
  const footerContinueAction = isMobile && mobileStep === "recap" ? continueToDetails : undefined;

  return (
    <WebshopLayout
      wide
      footer={{
        onBack: footerBackAction,
        onContinue: footerContinueAction,
        canContinue: true,
      }}>
      {checkoutContent}
    </WebshopLayout>
  );
}
